import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import {
  Feedback,
  BugReport,
  Brightness4,
  Brightness7,
  Close,
} from "@mui/icons-material";
import { useThemeMode } from "./ThemeModeContext";
import axiosInstance from "../Services/axiosConfig";
import Swal from "sweetalert2";

const FeedbackButton = () => {
  const { mode, toggleMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFeedback("");
    setError("");
  };

  const handleSendFeedback = async () => {
    if (!feedback.trim()) {
      setError("Feedback não pode estar vazio.");
      return;
    }

    setIsSending(true);
    try {
      const response = await axiosInstance.post("/feedback/", { message: feedback });
      if (response.data.status === "success") {
        Swal.fire({
          title: "Feedback enviado",
          text: "Agradecemos por compartilhar sua opinião.",
          icon: "success",
          confirmButtonText: "OK",
        });
        handleDialogClose();
      } else {
        Swal.fire({
          title: "Erro",
          text: "Falha ao enviar feedback.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Ocorreu um erro. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          borderRadius: "50%",
          width: 56,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: 3,
          zIndex: 1000,
          cursor: "pointer",
          transition: "transform 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.1)",
          },
        }}
        onClick={handleMenuOpen}
      >
        <Feedback fontSize="large" />
      </Box>

      {/* Menu for actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={toggleMode}>
          {mode === "light" ? (
            <>
              <Brightness4 sx={{ mr: 1 }} /> Modo Escuro
            </>
          ) : (
            <>
              <Brightness7 sx={{ mr: 1 }} /> Modo Claro
            </>
          )}
        </MenuItem>
        <MenuItem onClick={handleDialogOpen}>
          <BugReport sx={{ mr: 1 }} /> Enviar Feedback/Reportar Bug
        </MenuItem>
      </Menu>

      {/* Dialog for feedback */}
      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            position: "relative",
          },
        }}
      >
        <IconButton
          sx={{ position: "absolute", top: 8, right: 8 }}
          onClick={handleDialogClose}
        >
          <Close />
        </IconButton>
        <DialogTitle sx={{ pb: 1 }}>
          Enviar Feedback/Reportar Bug
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Ajude-nos a melhorar! Compartilhe sua opinião ou reporte um bug.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={feedback}
            onChange={(e) => {
              setFeedback(e.target.value);
              setError("");
            }}
            placeholder="Escreva seu feedback aqui ou reporte um bug aqui, por favor, de o maior número de detalhes possível."
            error={Boolean(error)}
            helperText={error}
            sx={{
              mt: 2,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ pt: 2 }}>
          <Button
            onClick={handleDialogClose}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendFeedback}
            color="primary"
            variant="contained"
            disabled={isSending}
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
